import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { formatBytes, nzbApi, type HistoryItem } from "@/api/nzb";
import { ServerChips } from "@/components/ServerChips";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

type Filter = "all" | "completed" | "failed";

export function NzbHistory() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<Filter>("all");

  const history = useQuery({
    queryKey: ["nzb", "history"],
    queryFn: nzbApi.history.list,
  });

  const filtered = useMemo(() => {
    const items = history.data ?? [];
    if (filter === "all") return items;
    return items.filter((i) => i.status === filter);
  }, [history.data, filter]);

  const removeMut = useMutation({
    mutationFn: (id: string) => nzbApi.history.remove(id),
    onSuccess: () => {
      toast("Entry removed", "success");
      qc.invalidateQueries({ queryKey: ["nzb", "history"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const clearMut = useMutation({
    mutationFn: () => nzbApi.history.clear(),
    onSuccess: () => {
      toast("History cleared", "success");
      qc.invalidateQueries({ queryKey: ["nzb", "history"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Clear failed", "error"),
  });

  const counts = useMemo(() => {
    const items = history.data ?? [];
    return {
      all: items.length,
      completed: items.filter((i) => i.status === "completed").length,
      failed: items.filter((i) => i.status === "failed").length,
    } as Record<Filter, number>;
  }, [history.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="History"
        description="Completed and failed downloads from this server."
        actions={
          <Button
            variant="danger"
            onClick={() => {
              if (confirm("Clear the entire download history?"))
                clearMut.mutate();
            }}
            disabled={!history.data || history.data.length === 0}
          >
            Clear all
          </Button>
        }
      />

      <FilterTabs value={filter} onChange={setFilter} counts={counts} />

      {history.isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : history.isError ? (
        <EmptyState
          title="NZB downloader is unavailable"
          description={
            history.error instanceof Error
              ? `Backend said: ${history.error.message}`
              : undefined
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={
            filter === "all" ? "No history yet" : `No ${filter} downloads`
          }
          description={
            filter === "all"
              ? "Completed downloads will show up here."
              : undefined
          }
        />
      ) : (
        <>
          {/* Mobile card list — the desktop table has 7 columns and is
              illegible on a phone. Cards stack the same fields. */}
          <ul className="space-y-2 md:hidden">
            {filtered.map((row) => (
              <HistoryCard
                key={`mc-${row.id}`}
                row={row}
                onRemove={() => {
                  if (confirm(`Remove "${row.name}" from history?`))
                    removeMut.mutate(row.id);
                }}
              />
            ))}
          </ul>
          <div className="hidden overflow-x-auto rounded border border-line md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Size</th>
                  <th className="px-4 py-2 font-medium">Completed</th>
                  <th className="px-4 py-2 font-medium">Error</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <HistoryRow
                    key={row.id}
                    row={row}
                    onRemove={() => {
                      if (confirm(`Remove "${row.name}" from history?`))
                        removeMut.mutate(row.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function HistoryCard({
  row,
  onRemove,
}: {
  row: HistoryItem;
  onRemove: () => void;
}) {
  return (
    <li className="rounded border border-line bg-surface/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="break-words font-medium text-sm leading-snug">
            {row.name}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground/70">
            <StatusBadge status={row.status} />
            {row.category && <span>{row.category}</span>}
            <span className="tabular-nums">{formatBytes(row.bytes)}</span>
            <span className="tabular-nums">
              {new Date(row.completed_at).toLocaleString()}
            </span>
          </div>
          {row.servers && row.servers.length > 0 && (
            <ServerChips servers={row.servers} className="mt-2" />
          )}
          {row.error && (
            <p className="mt-2 break-words text-[11px] text-red-300">
              {row.error}
            </p>
          )}
        </div>
        <Button variant="danger" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </li>
  );
}

function FilterTabs({
  value,
  onChange,
  counts,
}: {
  value: Filter;
  onChange: (next: Filter) => void;
  counts: Record<Filter, number>;
}) {
  const tabs: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "completed", label: "Completed" },
    { id: "failed", label: "Failed" },
  ];
  return (
    <div className="inline-flex rounded border border-line bg-surface/40 p-0.5 text-xs">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            "rounded px-3 py-1.5 transition",
            value === t.id
              ? "bg-accent/20 font-medium text-foreground"
              : "text-foreground/60 hover:text-foreground",
          )}
        >
          {t.label}
          <span className="ml-1.5 text-foreground/40">{counts[t.id]}</span>
        </button>
      ))}
    </div>
  );
}

function HistoryRow({
  row,
  onRemove,
}: {
  row: HistoryItem;
  onRemove: () => void;
}) {
  return (
    <tr className="border-t border-line">
      <td className="max-w-md px-4 py-2 align-top">
        <div className="truncate font-medium" title={row.name}>
          {row.name}
        </div>
        {/* Per-server tally: shows whether the primary delivered alone
            or a fallback rescued the job. The cascade signal is
            especially valuable on Failed rows — if every chip is your
            primary with nothing from the fallback, that's evidence the
            fallback didn't get a chance to try. */}
        {row.servers && row.servers.length > 0 && (
          <ServerChips servers={row.servers} className="mt-1.5" />
        )}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-4 py-2 text-foreground/70">{row.category ?? "—"}</td>
      <td className="px-4 py-2 text-xs text-foreground/70 tabular-nums">
        {formatBytes(row.bytes)}
      </td>
      <td className="px-4 py-2 text-xs text-foreground/60">
        {new Date(row.completed_at).toLocaleString()}
      </td>
      <td
        className="max-w-xs truncate px-4 py-2 text-xs text-red-300"
        title={row.error ?? undefined}
      >
        {row.error ?? ""}
      </td>
      <td className="px-4 py-2 text-right">
        <Button variant="danger" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: HistoryItem["status"] }) {
  return (
    <span
      className={cn(
        "rounded bg-surface-2/60 px-1.5 py-0.5 text-[10px] uppercase",
        status === "completed" ? "text-green-300" : "text-red-300",
      )}
    >
      {status}
    </span>
  );
}
